/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import EnterprisePageHeader from 'bundles/composable-page-header-enterprise/header-renderers/EnterprisePageHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import CoachChatPathways from 'bundles/unified-common/components/CoachChatPathways';
import InteractionGuard from 'bundles/unified-common/components/InteractionGuard';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';
import {
  GlobalCoachChatStateProvider,
  useGlobalCoachChatState,
} from 'bundles/unified-common/providers/GlobalCoachChatStateProvider';
import EnterpriseOrgScopedModalManager from 'bundles/unified-home-common/components/enterprise/modals/EnterpriseOrgScopedModalManager';
import OrgScopedAdminImpersonationBannerRenderer from 'bundles/unified-home-common/components/org-scoped/OrgScopedAdminImpersonationBannerRenderer';

import 'css!bundles/unified-home-common/__styles__/index';

type Props = {
  children: React.ReactNode;
  hideAdminImpersonationBanner?: boolean;
};

const styles = {
  coachModifiedStyle: css`
    width: calc(100% - 400px);
  `,
};

/*
Use this to add the header/footer to a page in the enterprise context.

Ensure this is nested beneath bundles/unified-common/providers/org-scoped/data/EnterpriseOrgScopedPageDataProvider
and bundles/unified-common/providers/org-scoped/modals/EnterpriseOrgScopedModalManagerProvider
*/
const EnterpriseOrgScopedHeaderFooterLayout = ({ children, hideAdminImpersonationBanner }: Props) => {
  const thirdPartyOrg = useThirdPartyOrganization();
  const headerRef = React.useRef<HTMLSpanElement>(null);
  const footerRef = React.useRef<HTMLDivElement>(null);
  const enableAiCoach = Boolean(useEnterpriseProductFeatures()?.enableAiCoach);
  const { coachOpen, pageChatType } = useGlobalCoachChatState();
  const isFullscreenCoachChatOpen = coachOpen && pageChatType === null;

  return (
    <React.Fragment>
      <span ref={headerRef}>
        <EnterprisePageHeader backupThirdPartyOrg={thirdPartyOrg} />
      </span>
      <UniversalPageFeatures authModalOptions={{ isEnterprise: true }} />
      {!hideAdminImpersonationBanner && <OrgScopedAdminImpersonationBannerRenderer />}
      <InteractionGuard
        preventInteraction={isFullscreenCoachChatOpen}
        as="main"
        css={coachOpen ? styles.coachModifiedStyle : undefined}
      >
        {children}
      </InteractionGuard>
      {enableAiCoach && <CoachChatPathways headerRef={headerRef} footerRef={footerRef} />}
      <PageFooter elRef={footerRef} hideTopBorder />
      <EnterpriseOrgScopedModalManager />
    </React.Fragment>
  );
};

const EnterpriseOrgScopedHeaderFooterWrapper = ({ children, hideAdminImpersonationBanner }: Props) => (
  <GlobalCoachChatStateProvider>
    <EnterpriseOrgScopedHeaderFooterLayout hideAdminImpersonationBanner={hideAdminImpersonationBanner}>
      {children}
    </EnterpriseOrgScopedHeaderFooterLayout>
  </GlobalCoachChatStateProvider>
);

export default EnterpriseOrgScopedHeaderFooterWrapper;
